import { useEffect, useRef, useState } from 'react';
import { Button, Card, CardTitle, EmptyState, RichTextEditor } from '@wundo/ui';
import { ArrowRight, Sparkles } from 'lucide-react';
import type {
  MeetingPageResponse,
  MeetingPrepContent,
} from '@wundo/shared/meetingPage.ts';
import { MeetingActionItems } from './MeetingActionItems.tsx';
import { MeetingCopyButton } from './MeetingCopyButton.tsx';
import { meetingIsOver } from './meetingIsOver.ts';
import { setChatDrawerState } from '@/lib/chatDrawerState.ts';
import { askChatForOpening } from '@/lib/chatOpeningAsk.ts';
import type { RichTextEditorWiring } from '@/lib/richTextEditor.ts';
import { m } from '@/paraglide/messages.js';

/* Long enough that a sentence is one save, short enough to feel instant. */
const SAVE_AFTER_MS = 600;

export interface MeetingPrepPanelProps {
  page: MeetingPageResponse;
  /** Whether a generation is in flight, so the panel can say so. */
  generating: boolean;
  generateFailed: boolean;
  /** Whether the copy just landed, so the button can say so for a moment. */
  copied: boolean;
  editor: RichTextEditorWiring;
  onGenerate: () => void;
  onRegenerate: () => void;
  onSave: (content: MeetingPrepContent) => void;
  onCopy: () => void;
  onToggleActionItem: (itemId: string, done: boolean) => void;
  onEditActionItem: (itemId: string, text: string) => void;
  onAddActionItem: (text: string) => void;
  onDeleteActionItem: (itemId: string) => void;
}

/**
 * The Agenda tab: what to raise in the conversation to come.
 *
 * The topics are a document the user owns: it is held in local state while
 * they type and written back on a short debounce, so a keystroke never waits
 * on the network and the caret never jumps. Under it, once there is a document
 * to put them under, the agreements still open on the conversation: a live read
 * of the thread the page never keeps a copy of. An empty state carries none of
 * them — there is nothing yet for them to stand under.
 *
 * Wundo's own suggestions are written the first time the tab is opened. That
 * fire-and-forget is safe to repeat: the server does nothing once it has
 * written one, which is why nothing here has to remember whether it already
 * asked.
 */
export function MeetingPrepPanel({
  page,
  generating,
  generateFailed,
  copied,
  editor,
  onGenerate,
  onRegenerate,
  onSave,
  onCopy,
  onToggleActionItem,
  onEditActionItem,
  onAddActionItem,
  onDeleteActionItem,
}: MeetingPrepPanelProps) {
  const [document, setDocument] = useState<MeetingPrepContent | null>(
    page.prep?.content ?? null,
  );
  /*
   * Whether the user has changes the server has not acknowledged. While that is
   * true a refetch must not overwrite what they are typing — the page is the
   * authority on its own document until the save lands.
   */
  const unsaved = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const asked = useRef(false);

  const fromServer = page.prep?.content ?? null;
  const stamp = fromServer ? JSON.stringify(fromServer) : null;

  useEffect(() => {
    if (unsaved.current) return;
    setDocument(stamp ? (JSON.parse(stamp) as MeetingPrepContent) : null);
  }, [stamp]);

  /*
   * Generated at first open — the other moment is the pre-meeting nudge.
   *
   * Where there is nothing to write one from the page says so itself, so firing
   * here would only spend a turn to be told the same thing.
   */
  useEffect(() => {
    if (asked.current || fromServer || !page.prep_available) return;
    asked.current = true;
    onGenerate();
  }, [fromServer, page.prep_available, onGenerate]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const write = (next: MeetingPrepContent) => {
    setDocument(next);
    unsaved.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      unsaved.current = false;
      onSave(next);
    }, SAVE_AFTER_MS);
  };

  /*
   * A meeting Wundo stays out of keeps the tab rather than losing it — the
   * switches above are right there, and a tab appearing under a flip would make
   * the page change shape while it is read. A meeting being asked about cannot
   * generate either, but it has something better than a reason to show.
   */
  if (!page.prep_available && !page.prep_intake && !document) {
    return <p className='mt-6 text-base text-ink-muted'>{unavailableReason(page)}</p>;
  }

  return (
    <div className='mt-6 flex flex-col gap-6'>
      {/*
        One card, not a stack of loose blocks: what the user carries into the
        room is a single document — the themes, and the agreements still open
        underneath them. The white surface is what makes it read as one.
      */}
      <Card as='section' padding='document'>
        <div className='flex items-center justify-between gap-3'>
          <CardTitle>{m.meeting_tab_prep()}</CardTitle>
          {document && <MeetingCopyButton copied={copied} onCopy={onCopy} />}
        </div>

        {/*
          Nothing to write a preparation from, and nothing written yet. The
          questions that would fill this page are being asked in the chat
          beside it, and that is the one place this state points: no blank
          page, no generate button — answering there is what fills this one.
        */}
        {!document && page.prep_intake && (
          <EmptyState
            headingLevel='h3'
            title={m.meeting_prep_intake_title()}
            description={m.meeting_prep_intake_body()}
            action={
              <Button
                size='sm'
                variant='ghost'
                /*
                 * Beside the page the drawer is open already, so opening it is
                 * no answer on its own: the question has to be put in front of
                 * the user, wherever they had got to in the chat.
                 */
                onClick={() => {
                  setChatDrawerState('open');
                  askChatForOpening();
                }}
              >
                {m.meeting_prep_intake_cta()}
                <ArrowRight className='size-4' aria-hidden='true' />
              </Button>
            }
          />
        )}

        {!document && !page.prep_intake && (
          <EmptyState
            headingLevel='h3'
            title={m.meeting_prep_invite_title()}
            busy={generating}
            description={
              generating ? m.meeting_prep_generating() : m.meeting_prep_invite_body()
            }
            action={
              <div className='flex flex-col items-center gap-2'>
                {generateFailed && (
                  <p role='alert' className='text-sm text-danger'>
                    {m.meeting_prep_generate_failed()}
                  </p>
                )}
                {/*
                  Writing one takes a while, and it is written here rather
                  than by reloading the page: the meeting, its details and its
                  agreements stay on screen and readable, and only this button
                  says that something is still coming.
                */}
                <Button size='sm' loading={generating} onClick={onGenerate}>
                  {!generating && <Sparkles className='size-4' aria-hidden='true' />}
                  {generateFailed
                    ? m.meeting_prep_generate_retry()
                    : m.meeting_prep_generate()}
                </Button>
              </div>
            }
          />
        )}

        {document && (
          <div className='mt-4'>
            {/*
              One document, not a stack of fields: the headings came from the
              template at generation time and belong to the user now, like
              every other line.
            */}
            <RichTextEditor
              variant='bare'
              contentClassName='text-ink-body'
              ariaLabel={m.meeting_prep_editor_label()}
              placeholder={m.meeting_prep_section_empty()}
              value={document.text}
              onChange={(text) => write({ text })}
              {...editor}
            />

            {/* A rule above the agreements: they belong to the conversation, not to a theme. */}
            <div className='mt-5 border-t border-fill pt-5'>
              <MeetingActionItems
                items={page.action_items}
                title={m.meeting_prep_actions_title()}
                emptyLabel={m.meeting_prep_actions_empty()}
                threadId={page.thread?.id ?? null}
                doneCount={page.action_items_done_count}
                onToggle={onToggleActionItem}
                onEdit={onEditActionItem}
                onAdd={onAddActionItem}
                onDelete={onDeleteActionItem}
              />
            </div>
          </div>
        )}

        {/*
          Writing the document again is only worth offering where writing it
          once was: with nothing to build from, that button could only come
          back empty handed — and would throw away what the user just typed.
        */}
        {document && (page.prep_available || generating || generateFailed) && (
          <div className='mt-5 flex flex-wrap items-center gap-3 border-t border-fill pt-4'>
            {page.prep_available && (
              <Button
                size='sm'
                variant='ghost'
                loading={generating}
                onClick={onRegenerate}
              >
                {!generating && <Sparkles className='size-4' aria-hidden='true' />}
                {m.meeting_prep_regenerate()}
              </Button>
            )}
            {generating && (
              <span className='text-sm text-ink-muted'>
                {m.meeting_prep_generating()}
              </span>
            )}
            {generateFailed && (
              <span role='alert' className='text-sm text-danger'>
                {m.meeting_prep_generate_failed()}
              </span>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

/*
 * Why nothing can be generated here, in one line. Over comes first: a meeting
 * that already happened is not prepared, whatever Wundo's part in it was.
 */
function unavailableReason(page: MeetingPageResponse): string {
  if (meetingIsOver(page, new Date())) return m.meeting_prep_meeting_over();
  return page.decision.follow
    ? m.meeting_prep_needs_thread()
    : m.meeting_prep_not_followed();
}
